import { TranslateService } from '@ngx-translate/core';

import {
  AfterContentInit,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Self,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { MatLegacyFormFieldControl as MatFormFieldControl } from '@angular/material/legacy-form-field';
import { MatLegacySelect as MatSelect } from '@angular/material/legacy-select';

import { Subject } from 'rxjs';

import { CountryModel, countryList } from './countries';
import { MwPhoneInputService } from './phone-input.service';

@Component({
  selector: 'mw-phone-input',
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.scss'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: MwPhoneInputComponent,
    },
  ],
})
export class MwPhoneInputComponent
  implements
    OnInit,
    OnDestroy,
    AfterContentInit,
    ControlValueAccessor,
    MatFormFieldControl<string>
{
  static nextId = 0;
  @HostBinding() id = `mw-phone-input-${MwPhoneInputComponent.nextId++}`;

  @HostBinding('attr.aria-describedby') describedBy = '';

  @ViewChild('countrySelectorRef', { static: true })
  countrySelector: MatSelect;
  @ViewChild('numberInputRef', { static: true })
  numberInput: ElementRef<any>;
  @ViewChild('containerRef', { static: true })
  containerDiv: ElementRef<any>;

  @Input()
  get placeholder(): string {
    return this.localPlaceholder;
  }
  set placeholder(value) {
    this.localPlaceholder = value;
    this.stateChanges.next();
  }

  get focused(): boolean {
    return this.countryPanelOpen || this.numberInputFocused;
  }

  get empty(): boolean {
    return this.value.length === 0;
  }

  get errorState(): boolean {
    if (this.ngControl) {
      return this.ngControl.invalid && this.ngControl.touched;
    } else {
      return false;
    }
  }

  @Input()
  get disabled(): boolean {
    return this.localDisabled;
  }
  set disabled(value: boolean) {
    this.localDisabled = value;
    this.stateChanges.next();
  }

  @Input()
  get required(): boolean {
    return this.localRequired;
  }
  set required(value) {
    this.localRequired = value;
    this.stateChanges.next();
  }

  @Input()
  get value(): string {
    const selectedCountry: CountryModel = this.countrySelector.value;
    if (selectedCountry && this.numberInput.nativeElement.value) {
      return `${selectedCountry.phoneCode}${this.numberInput.nativeElement.value}`;
    }
    return '';
  }
  set value(value: string) {
    if (!this.countrySelector || !this.numberInput) {
      this.initialValue = value;
      return;
    }
    if (!value) {
      this.countrySelector.value = this.defaultCountry;
      this.numberInput.nativeElement.value = '';
      return;
    }
    this.initialValue = null;
    let selectedCountry = this.countries.find((c) =>
      value.startsWith(c.phoneCode)
    );
    if (!selectedCountry) {
      selectedCountry = this.defaultCountry;
    }
    this.countrySelector.value = selectedCountry;
    const numberPart = value.replace(selectedCountry.phoneCode, '');
    this.numberInput.nativeElement.value = numberPart;
  }

  countries = countryList;

  countryPanelOpen = false;
  numberInputFocused = false;

  // Properties for ControlValueAccessor
  onTouchedCallback: () => void;
  onChangedCallback: (value: string) => void;

  // Properties for MaterialFormFieldControl
  shouldLabelFloat = true;
  controlType = 'mw-phone-input';
  autofilled = false;
  stateChanges = new Subject<void>();
  private localPlaceholder: string;
  private localRequired = false;
  private localDisabled = false;

  private defaultCountry = this.countries.find((c) => c.countryCode === 'no');
  private initialValue: string = this.defaultCountry.phoneCode;

  constructor(
    private translateService: TranslateService,
    @Optional() @Self() public ngControl: NgControl,
    private phoneInputService: MwPhoneInputService
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.phoneInputService.loadFlagIcons();
  }

  ngAfterContentInit(): void {
    if (this.initialValue) {
      this.value = this.initialValue;
    }
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
  }

  onDropdownPanelOpenChanged(isOpen: boolean): void {
    this.countryPanelOpen = isOpen;
  }

  onNumberInputFocusChanged(isFocused: boolean): void {
    this.numberInputFocused = isFocused;
    if (!isFocused) {
      this.onTouched();
    }
  }

  onChanged(): void {
    if (this.onChangedCallback) {
      this.onChangedCallback(this.value);
    }
    this.stateChanges.next();
  }

  onTouched(): void {
    if (this.onTouchedCallback) {
      this.onTouchedCallback();
    }
    this.stateChanges.next();
  }

  // For ControlValueAccessor
  writeValue(value: string): void {
    this.value = value;
  }
  registerOnChange(fn: () => void): void {
    this.onChangedCallback = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // For MatFormFieldControl
  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }
  onContainerClick(event: any): void {
    if (event.type === 'click') {
      const targetClass = event.target.className;
      if (
        targetClass === 'mat-form-field-flex' ||
        targetClass === 'mat-form-field-infix'
      ) {
        this.numberInput.nativeElement.focus();
      }
    }
  }
}
